import * as amplitude from '@amplitude/analytics-browser';
import {
  SpoArchitectureNaturesTableResponse,
  SpoSportsResponse,
} from '@spo/types';
import { useQueryClient } from '@tanstack/react-query';
import { VpButton, VpIcon, VpLoader, VpSticker } from '@vtmn-play/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyPool from '../../assets/empty-pool.png';
import { useRoles } from '../../contexts/RolesContext';
import { useNaturesArchitectureTable } from '../../hooks/useProductNatures';
import { ArchiNaturesTable } from '../archiNatureTable';
import { DrawerNature } from '../architecture/drawer/DrawerNature';
import { SportProductNaturesModal } from '../architecture/SportProductNaturesModal';
import './architecture-table.scss';

type ArchitectureTableProps = {
  sportId: number;
  sportData: SpoSportsResponse;
};

export const ArchitectureTable = ({
  sportId,
  sportData,
}: ArchitectureTableProps) => {
  const [searchedSportInput, setSearchedSportInput] = useState('');
  const [deletedNatureIds, setDeletedNatureIds] = useState<string[]>([]);
  const [drawerNatureId, setDrawerNatureId] = useState<string | null>(null);
  const [isProductNatureModalOpen, setIsProductNatureModalOpen] =
    useState(false);
  const { t } = useTranslation();
  const { hasRoleSportLeader } = useRoles();

  const {
    data: searchedArchiNaturesData,
    isLoading,
    status: archiTableFetchStatus,
  } = useNaturesArchitectureTable(sportId);

  const queryClient = useQueryClient();

  const { filteredNatures, filteredProductNatures } = useMemo(() => {
    const filteredData = searchedArchiNaturesData?.filter(
      (nature) =>
        !deletedNatureIds.includes(nature.id.toString()) &&
        (nature.label?.includes(searchedSportInput) ||
          nature.id.toString()?.includes(searchedSportInput)),
    );
    const filteredProductNatures =
      searchedArchiNaturesData?.map((sand) => sand.id) || [];
    return { filteredNatures: filteredData, filteredProductNatures };
  }, [searchedSportInput, searchedArchiNaturesData, deletedNatureIds]);

  const removeDeletedNature = (natureId: string) => {
    const shadowNatureIds = [...deletedNatureIds];
    if (!deletedNatureIds.includes(natureId)) {
      shadowNatureIds.push(natureId);
    } else {
      shadowNatureIds.splice(shadowNatureIds.indexOf(natureId), 1);
    }
    setDeletedNatureIds(shadowNatureIds);
  };

  const displayActiveNatureCount = () => {
    const activeNatures = searchedArchiNaturesData?.filter(
      (nat) => nat.ddfsStatus === 'active',
    );
    return `${activeNatures?.length} ${t('input.nature_label')}`;
  };

  const updateArchitectureData = (
    productNatureId: number,
    isEnabled: boolean,
  ) => {
    queryClient.setQueryData(
      ['useNaturesArchitectureTable', sportId],
      (oldData: SpoArchitectureNaturesTableResponse | undefined) =>
        oldData?.map((pn) =>
          pn.id === productNatureId ? { ...pn, enabled: isEnabled } : pn,
        ),
    );
  };

  return (
    <div
      data-testid="architecture-table-container"
      className="architecture-table-container"
    >
      {archiTableFetchStatus === 'pending' ||
      isLoading ||
      !searchedArchiNaturesData ? (
        <VpLoader />
      ) : (
        <>
          <DrawerNature
            natureId={drawerNatureId || ''}
            sportId={sportId}
            isOpen={Boolean(drawerNatureId)}
            closeDrawer={() => setDrawerNatureId(null)}
          />
          {sportData && sportData[0]?.configuration ? (
            <>
              <div className="architecture-table-header">
                <div className="archi-natures-input-search">
                  <VpIcon name="search" />
                  <input
                    type="search"
                    placeholder={t('architecture.search-nature')}
                    data-testid="architecture-search-nature-input"
                    onChange={(event) =>
                      setSearchedSportInput(event.currentTarget.value)
                    }
                    onFocus={() =>
                      amplitude.track('Architecture_Search-Nature')
                    }
                  />
                </div>
                <div className="architecture-buttons">
                  {hasRoleSportLeader && (
                    <VpButton
                      size="small"
                      shape="squared"
                      variant="primary"
                      data-testid="add_product-nature_cta"
                      aria-label="add product nature"
                      onClick={() => {
                        setIsProductNatureModalOpen(true);
                        amplitude.track('Architecture_Add-Product-Nature');
                      }}
                    >
                      <VpIcon name="add" size={16} />
                      {t('architecture.add-product-nature')}
                    </VpButton>
                  )}
                  <VpSticker>{displayActiveNatureCount()}</VpSticker>
                </div>
              </div>
              <SportProductNaturesModal
                isOpen={isProductNatureModalOpen}
                setIsOpenModal={setIsProductNatureModalOpen}
                sportId={sportId}
                productNaturesToHide={filteredProductNatures}
              />
              {filteredNatures && (
                <ArchiNaturesTable
                  natures={filteredNatures}
                  setDrawerNatureId={setDrawerNatureId}
                  removeDeletedNature={removeDeletedNature}
                  updateArchitectureData={updateArchitectureData}
                />
              )}
            </>
          ) : (
            <div
              className="empty-architecture"
              data-testid="empty-architecture"
            >
              <img src={emptyPool} alt="empty-illustration" />
              <div>{t('empty-zone')}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
